/// <amd-module name="Artifacts/Package/containers/UpstreamVersionsTabContainer" />

import { SearchUpstreamsIngestionPanelId } from "Artifacts/Package/components/SearchUpstreamsIngestionPanel";
import { UpstreamSourceDetailsPanelId } from "Artifacts/Package/components/UpstreamSourceDetailsPanel";
import { UpstreamVersionsTable } from "Artifacts/Package/components/UpstreamVersionsTable";
import { getUpstreamVersionsCommands } from "Artifacts/Package/helpers/PackageDetailsHelper";
import { IPackageDetailsModuleState } from "Artifacts/Package/redux";
import { PackageActions } from "Artifacts/Package/redux/Actions";
import * as Res from "Artifacts/Package/Resources";
import { ICommandControlProps } from "Artifacts/Package/types";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ComponentState, Protocol } from "Artifacts/Root/Constants";
import { decodeName } from "Artifacts/Root/helpers/PackageDecoderHelper";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { IProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { IPackageDetailsContainerRouteParams } from "Artifacts/Root/routing/Types";
import {
  UpstreamVersionsData,
  UpstreamVersionsDataVersionUpstream,
} from "Artifacts/Root/types/PackagingShared";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { useComponentContext } from "VSS/Platform/Layout";

const Actions = {
  initLoad: PackageActions.loadUpstreamVersions,
  showModal: RootActions.showModal,
  hideModal: RootActions.hideModal,
};

export interface IUpstreamVersionsTabContainerMapToStateProps {
  componentState: ComponentState;
  upstreamVersionsData?: UpstreamVersionsData;
}

const mapStateToProps = (
  state: IPackageDetailsModuleState,
): IUpstreamVersionsTabContainerMapToStateProps => {
  return {
    upstreamVersionsData: state.packageState.data.upstreamVersionsData,
    componentState: state.packageState.ui.upstreamVersionsComponentState,
  };
};

const UpstreamVersionsTabContainer: React.FC<IUpstreamVersionsTabContainerMapToStateProps &
  ICommandControlProps &
  typeof Actions> = ({
  upstreamVersionsData,
  componentState,
  initLoad,
  showModal,
  hideModal,
  setCommands,
}) => {
  const { packageName: packageNameFromUrl, protocol: protocolIdFromUrl } = useParams<
    IPackageDetailsContainerRouteParams
  >();

  const { feed } = useFeed();
  const { pageContext } = useComponentContext();
  const service: IProtocolService = pageContext.getService<IProtocolService>("IProtocolService");
  const protocol = protocolIdFromUrl && service.getProtocolById(protocolIdFromUrl);

  feed &&
    protocol &&
    setCommands(
      getUpstreamVersionsCommands(pageContext, protocol, feed, packageNameFromUrl, () => {
        initLoad(
          feed.id,
          protocolIdFromUrl,
          decodeName(packageNameFromUrl, protocolIdFromUrl),
          feed.project?.id,
        );
      }),
    );

  useEffect(() => {
    if (feed) {
      initLoad(
        feed.id,
        protocolIdFromUrl,
        decodeName(packageNameFromUrl, protocolIdFromUrl),
        feed.project?.id,
      );
    }
  }, [feed, initLoad, packageNameFromUrl, pageContext, protocolIdFromUrl, setCommands]);

  const supportedProtocolForIngestion =
    protocolIdFromUrl.toLocaleLowerCase() !== Protocol.Maven.toLocaleLowerCase() &&
    protocolIdFromUrl.toLocaleLowerCase() !== Protocol.Pypi.toLocaleLowerCase();

  const showIngestionModal = (normalizedVersion: string) => {
    upstreamVersionsData &&
      showModal({
        modalId: SearchUpstreamsIngestionPanelId,
        props: {
          onDismiss: hideModal,
          protocolName: protocolIdFromUrl,
          packageNormalizedName: upstreamVersionsData.packageNormalizedName,
          packageDisplayName: upstreamVersionsData.packageDisplayName,
          packageVersion: normalizedVersion,
        },
      });
  };

  const showUpstreamSourceDetailsPanel = (
    upstreamData: UpstreamVersionsDataVersionUpstream[],
    normalizedVersion: string,
    ingestIsDisabled: boolean,
    versionIsDeleted: boolean,
  ) => {
    upstreamVersionsData &&
      showModal({
        modalId: UpstreamSourceDetailsPanelId,
        props: {
          onDismiss: hideModal,
          packageDisplayName: upstreamVersionsData.packageDisplayName,
          normalizedPackageVersion: normalizedVersion,
          upstreamData: upstreamData,
          ingestIsDisabled: ingestIsDisabled,
          versionIsDeleted: versionIsDeleted,
        },
      });
  };

  return (
    <DefaultLoader state={componentState} primaryText={Res.UpstreamVersionsTable_NotFound}>
      {protocol && (
        <DynamicModuleLoader modules={[protocol.getReduxModule()]}>
          {feed && upstreamVersionsData && (
            <UpstreamVersionsTable
              upstreamData={upstreamVersionsData}
              showIngestionModal={showIngestionModal}
              showUpstreamSourceDetailsPanel={showUpstreamSourceDetailsPanel}
              supportedProtocolForIngestion={supportedProtocolForIngestion}
            />
          )}
        </DynamicModuleLoader>
      )}
    </DefaultLoader>
  );
};

const ConnectedUpstreamVersionsTabContainer = connect<
  IUpstreamVersionsTabContainerMapToStateProps,
  typeof Actions,
  ICommandControlProps
>(
  mapStateToProps,
  Actions,
)(UpstreamVersionsTabContainer);

export { ConnectedUpstreamVersionsTabContainer as UpstreamVersionsTabContainer };
